import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { config } from "../../Helpers/env";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import { Container, RightAlignedContainer } from "../Utils/styledComponents";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoneyBill } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../actions/snackbarActions";
import CustomTable from "../Utils/Tables/CustomTable";
import TextField from "../Utils/FormInputs/TextField";
import { DataExportButton } from "../Utils/Buttons/DataExportationButton";
import {
  useExcelReports,
  usePdfDownload,
} from "../Utils/ReusableComponents/useExcelExport";
import { TableBody, TableRow, TableCell } from "@material-ui/core";
import moment from "moment";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as BarTooltip,
} from "recharts";
import { formatDate } from "../Utils/ConvertDate";

// Colors for pie chart
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
];

const ExpenseReport = (props) => {
  const { actions, snackbars } = props;

  // State
  const [dateRange, setDateRange] = useState({
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().format("YYYY-MM-DD"),
  });
  const { start_date, end_date } = dateRange;
  // const [search_type, setSearchType] = useState('1'); // Removed as per request
  const [activeTab, setActiveTab] = useState(0); // 0=Table, 1=Graph
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    expenses: [],
    grand_total_base: 0,
    grand_total_by_currency: [],
    by_expense_account: [],
    period: {},
  });

  const {
    expenses,
    grand_total_base,
    grand_total_by_currency,
    by_expense_account,
    period,
  } = data;

  // Handlers
  const changeDate = (e) => {
    setDateRange({ ...dateRange, [e.target.name]: e.target.value });
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      // Search is handled by backend via 'search' param
      const res = await axios.get(
        `${config.smsUrl}/accounting/expenses/report`,
        {
          params: { start_date, end_date, search: searchTerm },
        },
      );
      setData(
        res.data.data || {
          expenses: [],
          grand_total_base: 0,
          grand_total_by_currency: [],
          by_expense_account: [],
          period: {},
        },
      );
      // actions.snackbarActions.snackSuccess("Report loaded");
    } catch (e) {
      console.error(e);
      actions.snackbarActions.snackError("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport();
    }, 500);
    return () => clearTimeout(timer);
  }, [start_date, end_date, searchTerm]);

  // Table Logic
  const headData = [
    { id: "expense_account_name", label: "Account Name", numeric: false },
    { id: "currency_symbol", label: "Currency", numeric: false },
    { id: "line_count", label: "Lines", numeric: false },
    { id: "total_original", label: "Total Original", numeric: false },
    { id: "total_base", label: "Total(USD)", numeric: false },
  ];

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Exports
  const exportParams = {
    start_date,
    end_date,
    search: searchTerm || undefined,
  };
  const { exportExcel, isLoading: isLoadingExcel } = useExcelReports(actions, {
    file_name: `Expense_Report_${start_date}_to_${end_date}`,
    params: { ...exportParams, format: "excel" },
    url: "accounting/expenses/report/export",
  });
  const { exportPdf, isLoading: isLoadingPdf } = usePdfDownload(actions, {
    file_name: `Expense_Report_${start_date}_to_${end_date}`,
    params: { ...exportParams, format: "pdf" },
    url: "accounting/expenses/report/export",
  });

  // Chart Data
  const pieData = (by_expense_account || [])
    .map((item) => ({
      name: item.expense_account_name || "Unknown",
      value: Number(item.total_base || 0),
    }))
    .filter((item) => item.value > 0);

  const dailyTrend = useMemo(() => {
    const map = new Map();
    (expenses || []).forEach((exp) => {
      const d = exp.date ? exp.date.substring(0, 10) : "Unknown";
      if (!map.has(d)) map.set(d, { date: d, total: 0 });
      map.get(d).total += Number(exp.amount_in_base_currency || 0);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [expenses]);

  const formatCurrency = (val) =>
    Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  return (
    <>
      <PageTitle title="Expenses Report" />
      <SubHeader title="Accounting" subTitle="Expenses Report">
        <FontAwesomeIcon icon={faMoneyBill} />
      </SubHeader>

      <Container className="mt-2">
        <MainSnackbar
          open={snackbars.openBar}
          variant={snackbars.type}
          message={snackbars.message}
          handleCloseBar={actions.snackbarActions.hideSnackbar}
        />

        {/* Filter & Actions */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row mb-0">
              <div className="col-md-8">
                <div className="row">
                  <div className="col-md-6">
                    {/* <label className="form-label small text-muted">Start Date</label> */}
                    <TextField
                      type="date"
                      name="start_date"
                      value={start_date}
                      onChange={changeDate}
                      id="start_date"
                    />
                  </div>
                  <div className="col-md-6">
                    {/* <label className="form-label small text-muted">End Date</label> */}
                    <TextField
                      type="date"
                      name="end_date"
                      value={end_date}
                      onChange={changeDate}
                      id="end_date"
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <RightAlignedContainer>
                  <DataExportButton
                    isLoading={isLoadingExcel || isLoadingPdf}
                    downLoadPdf={exportPdf}
                    exportCsv={exportExcel}
                  />
                </RightAlignedContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Stat Cards - White */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm bg-white text-dark">
              <div className="card-body text-center py-4">
                <h6 className="mb-2 text-muted text-uppercase small">
                  Total Expenses (USD)
                </h6>
                <h3 className="mb-0 fw-bold">
                  {formatCurrency(grand_total_base)}
                </h3>
              </div>
            </div>
          </div>
          {(grand_total_by_currency || []).map((curr, idx) => (
            <div key={idx} className="col-md-3">
              <div className="card border-0 shadow-sm bg-white text-dark">
                <div className="card-body text-center py-4">
                  <h6 className="mb-2 text-muted text-uppercase small">
                    Total in {curr.symbol || "Unknown"}
                  </h6>
                  <h3 className="mb-0 fw-bold">
                    {/* {curr.symbol} */}
                    {formatCurrency(curr.total)}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs Switcher */}
        <ul className="nav nav-tabs mb-3 border-bottom-0">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 0 ? "active fw-bold" : ""}`}
              onClick={() => setActiveTab(0)}
            >
              Tabular View
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 1 ? "active fw-bold" : ""}`}
              onClick={() => setActiveTab(1)}
            >
              Graphical View
            </button>
          </li>
        </ul>

        {/* Content */}
        <div className="bg-white rounded">
          {activeTab === 0 ? (
            <div className="p-2">
              <CustomTable
                title="Expenses by Category"
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                term={searchTerm}
                headData={headData}
                handler={(e) => setSearchTerm(e.target.value)}
                page={page}
                rowsPerPage={rowsPerPage}
                data={by_expense_account}
                colSpan={5}
              >
                {loading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : by_expense_account && by_expense_account.length > 0 ? (
                  <TableBody>
                    {by_expense_account
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((item, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            {item.expense_account_name || '-'}
                          </TableCell>
                          <TableCell>
                            {item.currency_symbol || '-'}
                          </TableCell>
                          <TableCell>
                            {item.line_count || 0}
                          </TableCell>
                          <TableCell>
                            {item.currency_symbol} {formatCurrency(item.total_original)}
                          </TableCell>
                          <TableCell>
                            USD {formatCurrency(item.total_base)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No records found
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </CustomTable>
            </div>
          ) : (
            <div className="row">
              {/* Pie Chart */}
              {/* <div className="col-12">
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white border-0">
                    <h6 className="mb-0">Expense Distribution by Category (USD)</h6>
                  </div>
                  <div className="card-body">
                    {pieData.length === 0 ? (
                      <div className="text-center py-5 text-muted">No data for chart</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={150}
                            fill="#8884d8"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div> */}

              {/* Daily Expenses Trend Bar Chart */}
              <div className="col-12">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0">
                    <h6 className="mb-0">Daily Trend</h6>
                  </div>
                  <div className="card-body">
                    {loading ? (
                      <div className="text-center py-5">Loading...</div>
                    ) : dailyTrend.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        No daily data available
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={dailyTrend}
                          margin={{ top: 10, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis tickFormatter={(val) => val.toLocaleString()} />
                          <BarTooltip
                            formatter={(val) => [formatCurrency(val), 'Total']}
                          />
                          <Bar
                            dataKey="total"
                            fill="#0d6efd"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({ snackbars: state.snackbar });
const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpenseReport);
